import { useCallback, useEffect, useRef, useState } from "react";
import { usePermissions } from "../providers/authProvider";
import { Api } from "../api";
import { OrderData } from "../../../types/order";
import { SITE_DATA_PARTITIONS } from "../../../types";
import { ShipmentData } from "../../../types/shipments";

export type RequestStatus =
  | "idle"
  | "server-error"
  | "present"
  | "no-slug"
  | "request-error"
  | "loading";

export type ShipmentsRequest = {
  status: RequestStatus;
  shipments?: ShipmentData[];
  orderSlug?: string;
  error?: any;
};

export type ShipmentRequest = {
  status: RequestStatus;
  shipment?: ShipmentData;
  orderSlug?: string;
  shipmentSlug?: string;
  error?: any;
};

export type OrderRequest = {
  status: RequestStatus;
  order?: OrderData;
  shipmentsRequest?: ShipmentsRequest;
  orderSlug?: string;
  error?: any;
};
export const useOrder = ({
  slug,
  permissions,
}: {
  slug?: string;
  permissions?: ReturnType<typeof usePermissions>;
}) => {
  const shouldRequestOrderData = useRef(!!slug);
  useEffect(() => {
    if (slug) {
      shouldRequestOrderData.current = true;
    }
  }, [slug]);
  const [orderRequest, setOrderRequest] = useState<OrderRequest>({
    status: shouldRequestOrderData.current ? "loading" : "no-slug",
  });
  const request = useCallback(() => {
    if (slug && !permissions?.loading) {

      setOrderRequest({
        status: slug ? "loading" : "no-slug",
      });

      Api.get<OrderData>({
        type: SITE_DATA_PARTITIONS.ORDER,
        slug,
      })
        .then((resp) =>
          resp.success
            ? setOrderRequest((req) => ({
                status: "present" as const,
                order: resp.data,
                orderSlug: slug,
                shipmentsRequest: req.shipmentsRequest,
              }))
            : setOrderRequest((req) => ({
                status: "server-error" as const,
                error: resp,
                orderSlug: slug,
                shipmentsRequest: req.shipmentsRequest,
              })),
        )
        .catch((e) =>
          setOrderRequest((req) => ({
            status: "request-error" as const,
            error: e,
            orderSlug: slug,
            shipmentsRequest: req.shipmentsRequest,
          })),
        );

      Api.getRelated<ShipmentData>({
        type: SITE_DATA_PARTITIONS.ORDER,
        slug: slug,
        subType: SITE_DATA_PARTITIONS.SHIPMENT,
      })
        .then((resp) =>
          resp.success
            ? setOrderRequest((req) => ({
                ...req,
                shipmentsRequest: {
                  status: "present" as const,
                  shipments: resp.body.items ?? [],
                  orderSlug: slug,
                },
              }))
            : setOrderRequest((req) => ({
                ...req,
                shipmentsRequest: {
                  status: "server-error" as const,
                  error: resp.error,
                  orderSlug: slug,
                },
              })),
        )
        .catch((e) =>
          setOrderRequest((req) => ({
            ...req,
            shipmentsRequest: {
              status: "request-error" as const,
              error: e,
              orderSlug: slug,
            },
          })),
        );
    }
  }, [slug, permissions?.loading, setOrderRequest]);

  useEffect(() => {
    if (shouldRequestOrderData.current && slug && !permissions?.loading) {
      shouldRequestOrderData.current = false;
      request();
    }
  }, [shouldRequestOrderData, request, slug, permissions?.loading]);
  return [orderRequest, request] as const;
};
